/**
 * Client-side image compression for uploads.
 *
 * Why this exists: a modern phone photo is 4-12MB, and the backend resizes every
 * uploaded JPEG/PNG down to 800px wide at quality 80 before it ever reaches
 * SharePoint (see `resizeImageIfNeeded` in yallyhub-be/src/file/file.service.ts).
 * Sending the full-resolution original therefore buys nothing - it only makes the
 * user wait. Shrinking in the browser first cuts the bytes on the wire by 5-20x
 * with zero loss in what actually gets stored.
 *
 * Two details that are easy to get wrong and are handled here:
 *
 * 1. EXIF orientation. We bake the rotation into the pixels, so the stored image
 *    is upright even though the backend's `sharp` pipeline does not auto-rotate.
 * 2. EXIF metadata. `canvas.toBlob()` drops all metadata, which would silently
 *    break the backend's `extractExifData` (photo date + GPS land in the FileLog).
 *    We therefore copy the original APP1/Exif segment into the compressed JPEG,
 *    with the Orientation tag rewritten to 1 so the baked-in rotation is not
 *    applied a second time by a downstream viewer.
 *
 * No external dependencies: canvas + a small amount of JPEG marker parsing.
 */

import * as Sentry from "@sentry/react";

/** MIME types we are willing to re-encode. */
const COMPRESSIBLE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * HEIC/HEIF are deliberately excluded: only Safari can decode them, and
 * converting would change the file extension that the backend uses to resolve
 * the SharePoint filename. Animated GIFs would lose their animation and SVG is
 * vector, so both are left alone too.
 */
const NEVER_COMPRESS_MIME_TYPES = [
  "image/gif",
  "image/svg+xml",
  "image/heic",
  "image/heif",
  "image/tiff",
];

/**
 * Whether this file will be shrunk in the browser before upload.
 *
 * Callers use this to relax their pre-upload size limits: a 40MB phone photo is
 * fine because it leaves here at roughly 1MB, whereas a 40MB PDF is not.
 */
export const isClientCompressibleImage = (file: File): boolean =>
  COMPRESSIBLE_MIME_TYPES.includes(file.type) &&
  !NEVER_COMPRESS_MIME_TYPES.includes(file.type);

export interface ImageCompressionOptions {
  /**
   * Longest edge of the output, in pixels. Default 2560, comfortably above the
   * 800px the backend downsizes to, so raising the backend limit later cannot be
   * capped by what the browser sent.
   */
  maxDimension?: number;
  /** JPEG/WebP quality, 0-1. Default 0.82. */
  quality?: number;
  /** Files at or below this size are left untouched. Default 512KB. */
  minSizeBytes?: number;
}

export interface ImageCompressionResult {
  /** The file to upload - the compressed one, or the original when skipped. */
  file: File;
  originalSize: number;
  compressedSize: number;
  wasCompressed: boolean;
  /** Set when the original was returned unchanged, for logging and the UI. */
  skipReason?: string;
}

const DEFAULTS: Required<ImageCompressionOptions> = {
  maxDimension: 2560,
  quality: 0.82,
  minSizeBytes: 512 * 1024,
};

/**
 * Reads a blob's bytes.
 *
 * `Blob.arrayBuffer()` is the modern path, but it is missing on older Safari (and
 * in jsdom), so fall back to FileReader rather than silently losing EXIF.
 */
const readBlobBytes = (blob: Blob): Promise<Uint8Array> => {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () =>
      reject(reader.error ?? new Error("Kon bestand niet lezen"));
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Reads the EXIF Orientation tag (0x0112) from a JPEG and returns the raw APP1
 * segment so it can be re-attached after re-encoding.
 *
 * Returns orientation 1 and no segment for anything that is not a JPEG with an
 * Exif APP1 block - callers treat that as "nothing to preserve", never an error.
 */
export const readJpegExif = async (
  file: File,
): Promise<{ orientation: number; app1Segment: Uint8Array | null }> => {
  const empty = { orientation: 1, app1Segment: null };

  try {
    // EXIF lives at the very front of the file; 256KB is a generous window that
    // still covers an embedded thumbnail.
    const head = await readBlobBytes(file.slice(0, 256 * 1024));
    const view = new DataView(head.buffer, head.byteOffset, head.byteLength);

    if (head.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) {
      return empty; // Not a JPEG (no Start Of Image marker).
    }

    let offset = 2;
    while (offset + 4 <= head.byteLength) {
      const marker = view.getUint16(offset, false);

      // Anything not starting with 0xFF means we lost sync; bail out safely.
      if ((marker & 0xff00) !== 0xff00) return empty;

      const segmentLength = view.getUint16(offset + 2, false);
      if (segmentLength < 2) return empty;

      if (marker === 0xffe1) {
        const segmentEnd = offset + 2 + segmentLength;
        if (segmentEnd > head.byteLength) return empty; // Truncated by our slice.

        // Verify the "Exif\0\0" header before trusting the payload.
        const exifHeaderOffset = offset + 4;
        if (
          exifHeaderOffset + 6 > head.byteLength ||
          view.getUint32(exifHeaderOffset, false) !== 0x45786966
        ) {
          offset = segmentEnd;
          continue; // Some other APP1 flavour (e.g. XMP); keep looking.
        }

        const app1Segment = head.slice(offset, segmentEnd);
        return {
          orientation: readOrientationFromApp1(app1Segment) ?? 1,
          app1Segment,
        };
      }

      // Start Of Scan - image data begins, no metadata beyond this point.
      if (marker === 0xffda) return empty;

      offset += 2 + segmentLength;
    }

    return empty;
  } catch (error) {
    // Metadata is a nice-to-have; never let it block an upload.
    Sentry.captureException(error, {
      level: "warning",
      extra: {
        context: "imageCompression.readJpegExif",
        fileName: file.name,
        fileSize: file.size,
      },
    });
    return empty;
  }
};

/**
 * Locates the TIFF header inside an APP1 segment and walks IFD0.
 * `mutateOrientationTo` rewrites the tag in place when provided.
 */
const walkApp1Orientation = (
  app1Segment: Uint8Array,
  mutateOrientationTo?: number,
): number | null => {
  // Layout: FFE1 | length(2) | "Exif\0\0"(6) | TIFF header
  const tiffStart = 10;
  if (app1Segment.byteLength < tiffStart + 8) return null;

  const view = new DataView(
    app1Segment.buffer,
    app1Segment.byteOffset,
    app1Segment.byteLength,
  );

  const byteOrder = view.getUint16(tiffStart, false);
  if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return null;
  const littleEndian = byteOrder === 0x4949;

  const ifd0Offset = tiffStart + view.getUint32(tiffStart + 4, littleEndian);
  if (ifd0Offset + 2 > app1Segment.byteLength) return null;

  const entryCount = view.getUint16(ifd0Offset, littleEndian);
  for (let i = 0; i < entryCount; i++) {
    const entryOffset = ifd0Offset + 2 + i * 12;
    if (entryOffset + 12 > app1Segment.byteLength) return null;

    if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
      if (mutateOrientationTo !== undefined) {
        view.setUint16(entryOffset + 8, mutateOrientationTo, littleEndian);
      }
      return view.getUint16(entryOffset + 8, littleEndian);
    }
  }

  return null;
};

const readOrientationFromApp1 = (app1Segment: Uint8Array): number | null =>
  walkApp1Orientation(app1Segment);

/**
 * Splices an APP1 segment into a freshly encoded JPEG, directly after the SOI
 * marker, and rewrites Orientation to 1 because the rotation is already baked
 * into the pixels we just drew.
 */
export const injectExifIntoJpeg = (
  jpegBytes: Uint8Array,
  app1Segment: Uint8Array,
): Uint8Array<ArrayBuffer> => {
  if (
    jpegBytes.byteLength < 2 ||
    jpegBytes[0] !== 0xff ||
    jpegBytes[1] !== 0xd8
  ) {
    // Not a JPEG we recognise; hand back the bytes untouched. Copied rather than
    // returned as-is so the result always owns a plain ArrayBuffer.
    return new Uint8Array(jpegBytes);
  }

  // Work on a copy so the caller's original EXIF bytes stay untouched.
  const patchedApp1 = app1Segment.slice();
  walkApp1Orientation(patchedApp1, 1);

  const out = new Uint8Array(jpegBytes.byteLength + patchedApp1.byteLength);
  out.set(jpegBytes.subarray(0, 2), 0); // SOI
  out.set(patchedApp1, 2);
  out.set(jpegBytes.subarray(2), 2 + patchedApp1.byteLength);
  return out;
};

/**
 * Canvas transform that undoes a given EXIF orientation.
 *
 * `canvasWidth`/`canvasHeight` are the *upright* output extents (i.e. the canvas'
 * own size). The bitmap is then drawn at its pre-rotation extents, which the
 * matrix maps onto the full canvas.
 */
const applyOrientationTransform = (
  ctx: CanvasRenderingContext2D,
  orientation: number,
  canvasWidth: number,
  canvasHeight: number,
) => {
  switch (orientation) {
    case 2: // Flip horizontal
      ctx.transform(-1, 0, 0, 1, canvasWidth, 0);
      break;
    case 3: // Rotate 180
      ctx.transform(-1, 0, 0, -1, canvasWidth, canvasHeight);
      break;
    case 4: // Flip vertical
      ctx.transform(1, 0, 0, -1, 0, canvasHeight);
      break;
    case 5: // Transpose
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6: // Rotate 90 CW
      ctx.transform(0, 1, -1, 0, canvasWidth, 0);
      break;
    case 7: // Transverse
      ctx.transform(0, -1, -1, 0, canvasWidth, canvasHeight);
      break;
    case 8: // Rotate 90 CCW
      ctx.transform(0, -1, 1, 0, 0, canvasHeight);
      break;
    default:
      break; // 1 and anything unexpected: draw as-is.
  }
};

/** Orientations 5-8 rotate by 90 degrees, so output width/height swap. */
const swapsAxes = (orientation: number) => orientation >= 5 && orientation <= 8;

/**
 * Decodes to an ImageBitmap without letting the browser apply EXIF rotation -
 * we apply it ourselves so behaviour is identical across browsers.
 */
const decodeBitmap = async (file: File): Promise<ImageBitmap> => {
  if (typeof createImageBitmap !== "function") {
    throw new Error("createImageBitmap is not available in this browser");
  }
  return createImageBitmap(file, { imageOrientation: "none" });
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error(`canvas.toBlob returned null for ${mimeType}`)),
      mimeType,
      quality,
    );
  });

const replaceExtension = (fileName: string, mimeType: string): string => {
  if (mimeType !== "image/jpeg") return fileName;
  return fileName.replace(/\.(png|webp)$/i, ".jpg");
};

/**
 * Compresses a single image file. Never throws: on any failure the original file
 * is returned with a `skipReason`, and the error is reported to Sentry so we can
 * see which formats or browsers fall through.
 */
export const compressImageFile = async (
  file: File,
  options: ImageCompressionOptions = {},
): Promise<ImageCompressionResult> => {
  const { maxDimension, quality, minSizeBytes } = { ...DEFAULTS, ...options };
  const originalSize = file.size;
  const skip = (skipReason: string): ImageCompressionResult => ({
    file,
    originalSize,
    compressedSize: originalSize,
    wasCompressed: false,
    skipReason,
  });

  if (NEVER_COMPRESS_MIME_TYPES.includes(file.type)) {
    return skip(`formaat ${file.type} wordt niet gecomprimeerd`);
  }
  if (!COMPRESSIBLE_MIME_TYPES.includes(file.type)) {
    return skip("geen comprimeerbare afbeelding");
  }
  if (originalSize <= minSizeBytes) {
    return skip("bestand is al klein genoeg");
  }

  let bitmap: ImageBitmap | null = null;
  try {
    const { orientation, app1Segment } = await readJpegExif(file);
    bitmap = await decodeBitmap(file);

    // Dimensions after rotation, before scaling.
    const uprightWidth = swapsAxes(orientation) ? bitmap.height : bitmap.width;
    const uprightHeight = swapsAxes(orientation) ? bitmap.width : bitmap.height;

    const scale = Math.min(
      1,
      maxDimension / Math.max(uprightWidth, uprightHeight),
    );

    // Nothing to gain from re-encoding an already-small, already-upright image.
    if (scale === 1 && orientation === 1) {
      return skip("afmetingen zijn al binnen de limiet");
    }

    const targetWidth = Math.max(1, Math.round(uprightWidth * scale));
    const targetHeight = Math.max(1, Math.round(uprightHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not acquire a 2D canvas context");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // The matrix is expressed in canvas (upright) extents; the bitmap is drawn at
    // its pre-rotation extents, which for orientations 5-8 are the swapped pair.
    applyOrientationTransform(ctx, orientation, targetWidth, targetHeight);
    const drawWidth = swapsAxes(orientation) ? targetHeight : targetWidth;
    const drawHeight = swapsAxes(orientation) ? targetWidth : targetHeight;
    ctx.drawImage(bitmap, 0, 0, drawWidth, drawHeight);

    // PNG keeps its format so transparency survives; the size win comes from the
    // smaller dimensions. JPEG and WebP are re-encoded lossily.
    const outputMime = file.type === "image/png" ? "image/png" : file.type;
    const blob = await canvasToBlob(canvas, outputMime, quality);

    // Only touch the raw bytes when there is EXIF to re-attach; otherwise the
    // blob goes straight into the File.
    const payload: BlobPart =
      outputMime === "image/jpeg" && app1Segment
        ? injectExifIntoJpeg(await readBlobBytes(blob), app1Segment)
        : blob;

    const compressedFile = new File(
      [payload],
      replaceExtension(file.name, outputMime),
      { type: outputMime, lastModified: file.lastModified },
    );

    // Re-encoding can inflate a file (already-optimised JPEGs, flat PNGs). If it
    // did, the original is strictly the better thing to upload.
    if (compressedFile.size >= originalSize) {
      return skip("comprimeren leverde geen winst op");
    }

    return {
      file: compressedFile,
      originalSize,
      compressedSize: compressedFile.size,
      wasCompressed: true,
    };
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: "imageCompression.compressImageFile",
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        maxDimension,
        quality,
      },
    });
    console.warn(
      `[imageCompression] Kon ${file.name} niet comprimeren, origineel wordt geüpload:`,
      error,
    );
    return skip("comprimeren mislukt");
  } finally {
    bitmap?.close?.();
  }
};

/** Human-readable summary of what compression saved, for logs and the UI. */
export const describeCompression = (
  result: Pick<
    ImageCompressionResult,
    "originalSize" | "compressedSize" | "wasCompressed"
  >,
): string | null => {
  if (!result.wasCompressed || result.originalSize === 0) return null;
  const saved = 1 - result.compressedSize / result.originalSize;
  if (saved <= 0.02) return null;
  return `${Math.round(saved * 100)}% kleiner`;
};
